import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FlexBox, Text } from "@styles";
import Link from "@components/Link";
import dayjs from "dayjs";
import RichText from "@components/RichText";
import { FaRegPlayCircle } from "react-icons/fa";
import { searchIconMapping } from "@components/IconExplanation/data";
import { PageProps } from "gatsby";
import Pagination, { PaginationProps } from "./Pagination";

interface GridProps {
  itemSize: number;
  itemGap: number;
  autoFill: boolean;
}

const Grid = styled.section<GridProps>`
  display: grid;
  grid-template-columns: ${({ itemSize, autoFill }) =>
    itemSize && `repeat(${autoFill ? "auto-fill" : "auto-fit"}, minmax(${itemSize}px, 1fr))`};
  gap: ${({ itemGap }) => itemGap && `${itemGap}rem`};
`;

export interface FlatListItemProps {
  id: string;
  slug: string;
  title?: string | null;
  description?: string | null;
  image?: IGatsbyImageData;
  imageUrl?: string | null;
  date?: string | null;
  itemFileType?: string;
  type?: string;
  sort?: number;
}

interface FlatListProps {
  title?: string;
  items: FlatListItemProps[];
  showDescription?: boolean;
  itemSize?: number;
  itemGap?: number;
  listFileType?: "video" | "image";
  pagination?: PaginationProps;
  autoFill?: boolean;
  showIcon?: boolean;
  showPagination?: boolean;
  location?: PageProps["location"];
}

const VideoPlayButton = styled.span`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.35);
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FlatList = ({
  title,
  items,
  showDescription = true,
  itemSize = 250,
  itemGap = 1,
  autoFill = false,
  listFileType = "image",
  pagination,
  showIcon = false,
  location,
  showPagination = false,
}: FlatListProps) => {
  const params = new URLSearchParams(location?.search);
  const pageQuery = params.has("page") ? Number(params.get("page")) : null;
  const [page, setPage] = useState(pageQuery ?? 1);
  const numOfPages = Math.ceil(items.length / 12);

  useEffect(() => {
    if (pageQuery) {
      setPage(pageQuery);
    } else {
      setPage(1);
    }
  }, [pageQuery]);

  if(!items.length) {
    return <p>No items found</p>;
  }

  return (



    <FlexBox direction="column">
      {title && (
        <Text as="h3" size={1.75} color="#575757">
          {title}
        </Text>
      )}

      <Grid itemSize={itemSize} itemGap={itemGap} autoFill={autoFill} style={{ width: "100%" }}>
        {items
          .slice((page - 1) * 12, 12 * page)
          .map(({ id, title: articleTitle, slug, description, image, imageUrl, date, itemFileType = "image" }) => {
            let contentType = searchIconMapping[itemFileType] ?? null;
            if (!contentType) contentType = searchIconMapping.page;

            let imageRender = "icon";
            if (image) imageRender = "gatsbyImage";
            else if (imageUrl) imageRender = "imageUrl";

            return (
              <Link to={slug} key={id} customStyle={{ maxWidth: "500px" }}>
                <FlexBox direction="column" style={{ height: "100%" }}>
                  <FlexBox style={{ maxHeight: 300 }} relative>
                    {imageRender === "gatsbyImage" && (
                      <GatsbyImage style={{ aspectRatio: 16 / 9 }} image={image ?? ({} as IGatsbyImageData)} alt={title ?? ""} />
                    )}
                    {imageRender === "imageUrl" && imageUrl && (
                      <FlexBox
                        style={{ aspectRatio: 16 / 9 }}
                        direction="column"
                        alignItems="center"
                        width="100%"
                        justifyContent="center"
                        // border="1px solid #eee"
                        radius={0.25}
                      >
                        <img style={{ maxWidth: "100%", aspectRatio: 16 / 9, objectFit: "contain" }} src={imageUrl} alt={title ?? ""} />
                      </FlexBox>
                    )}
                    {imageRender === "icon" && (
                      <FlexBox
                        style={{ aspectRatio: 16 / 9 }}
                        direction="column"
                        alignItems="center"
                        width="100%"
                        justifyContent="center"
                        border="1px solid #eee"
                        radius={0.25}
                      >
                        <img src={contentType?.image} width={50} height={50} alt={contentType?.name} />
                      </FlexBox>
                    )}

                    {(listFileType === "video" || itemFileType === "video") && (image || imageUrl) && (
                      <VideoPlayButton>
                        <FaRegPlayCircle size={80} color="#fff" />
                      </VideoPlayButton>
                    )}
                    {listFileType !== "video" && itemFileType !== "video" && (image || imageUrl) && showIcon && (
                      <FlexBox
                        style={{ position: "absolute", bottom: 0, borderTopRightRadius: "0.25rem" }}
                        backgroundColor="#fff"
                        padding={0.5}
                      >
                        <img src={contentType?.image} width={45} height={45} alt={contentType?.name} />
                      </FlexBox>
                    )}
                  </FlexBox>

                  <FlexBox direction="column" gap={0.75}>
                    {articleTitle ? (
                      <Text size={1.15} as="h2" line={3} paddingBottom={0.75}>
                        {articleTitle}
                      </Text>
                    ) : null}

                    {date && <Text paddingBottom={1.25}>{dayjs(date).format("MMMM DD, YYYY")}</Text>}

                    {showDescription && (
                      <RichText
                        body={(description?.length ?? 0) < 150 ? description : `${description?.substring(0, 150)}...`}
                        
                        customStyle={{ marginTop: -20 }}
                      />
                    )}
                  </FlexBox>
                </FlexBox>
              </Link>
            );
          })}
      </Grid>

      {showPagination && numOfPages && !pagination?.currentPage ? (
        <Pagination
          currentPage={page}
          isFirst={page === 1}
          isLast={page === numOfPages}
          numOfPages={numOfPages}
          prefix={`${location?.pathname}${location?.search}` }
          mode="client"
        />
      ) : null}

      {pagination?.currentPage ? <Pagination {...pagination} /> : null}
    </FlexBox>
  );
};

export default FlatList;
